
import React from "react";
import { Container } from "react-bootstrap";
import { Helmet } from "react-helmet";
import "./Accessibility.css";

export const Accessibility = () => {
  const pageUrl = "https://rishuy-asakim.co.il/accessibility";

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "נגישות עסקים ורישוי עסק – דרישות החוק ואישורי נגישות",
    description:
      "מדריך בנושא נגישות לעסקים בישראל, מורשה נגישות מתו״ס, מורשה נגישות השירות, התאמות נגישות ותהליך בדיקת הנגישות במסגרת רישוי עסקים.",
    mainEntityOfPage: pageUrl,
    author: {
      "@type": "Organization",
      name: "רישוי עסקים ישראל",
      url: "https://rishuy-asakim.co.il/"
    },
    publisher: {
      "@type": "Organization",
      name: "רישוי עסקים ישראל",
      url: "https://rishuy-asakim.co.il/"
    },
    inLanguage: "he-IL"
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "ראשי",
        item: "https://rishuy-asakim.co.il/"
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "נגישות עסקים",
        item: pageUrl
      }
    ]
  };

  return (
    <Container fluid className="accessibility-container">
      <Helmet>
        <title>
          נגישות עסקים ורישוי עסק | דרישות החוק ואישורי נגישות
        </title>

        <meta
          name="description"
          content="מדריך נגישות לעסקים בישראל: מורשה נגישות מתו״ס, מורשה נגישות השירות, התאמות נגישות לעסק ותהליך הבדיקה במסגרת רישוי עסקים."
        />

        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="article" />
        <meta
          property="og:title"
          content="נגישות עסקים ורישוי עסק | רישוי עסקים ישראל"
        />
        <meta
          property="og:description"
          content="כל מה שצריך לדעת על דרישות נגישות לעסקים, מורשי נגישות והתאמות נגישות במסגרת תהליך רישוי עסק."
        />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:locale" content="he_IL" />

        <meta name="robots" content="index, follow" />

        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <div className="accessibility-page">
        <div className="accessibility-card">
          <div className="accessibility-decorator"></div>

          <div className="accessibility-content">

            <p className="accessibility-highlight">
              דרישות נגישות לעסקים במסגרת תהליך קבלת היתר זמני, רישיון עסק או חידוש רישיון:
            </p>

            <h1 className="accessibility-main-title">
              נגישות עסקים ורישוי עסק – דרישות החוק ואישורי נגישות
            </h1>

            <p className="accessibility-main-text">
              נגישות לעסקים היא חלק חשוב מתהליך רישוי העסק בישראל.
              בהתאם לסוג העסק, לאופי השירות ולדרישות הדין, יש לבחון
              את הצורך בהתאמות נגישות למבנה ולשירות, בהתאם לחוק
              <span className="accessibility-bold-blue">
                {" "}שוויון זכויות לאנשים עם מוגבלויות{" "}
              </span>
              ותקנותיו.
            </p>

            <div className="accessibility-info-box">

              <p className="accessibility-intro-text">
                מתכננים לפתוח עסק חדש? צריכים לחדש את רישיון העסק הקיים
                או לקבל היתר זמני?
                <strong>
                  {" "}חשוב לבדוק מראש את דרישות הנגישות החלות על העסק
                  ולהיערך לביצוע ההתאמות הנדרשות בהתאם להוראות הדין.
                </strong>
              </p>

              <p className="accessibility-info-text">
                חוק שוויון זכויות לאנשים עם מוגבלויות ותקנות הנגישות
                קובעים דרישות להנגשת מקומות ושירותים ציבוריים.
                היקף החובות וההתאמות הנדרשות משתנה בהתאם לסוג העסק,
                מאפייניו והוראות הדין החלות עליו.
              </p>

              <p className="accessibility-info-text">
                במסגרת בדיקת נגישות לעסק עשויה להידרש התייחסות
                מקצועית של מורשי נגישות בתחומים שונים, בהתאם
                לדרישות החלות על העסק:
              </p>

              <div className="accessibility-expert-box">

                <div className="accessibility-expert-number">
                  1
                </div>

                <div className="accessibility-expert-content">

                  <h2 className="accessibility-expert-title">
                    מורשה נגישות מתו"ס – נגישות מבנים ותשתיות
                  </h2>

                  <p className="accessibility-small-text">
                    מבנים, תשתיות וסביבה
                  </p>

                  <p className="accessibility-info-text">
                    מורשה נגישות מתו"ס עוסק בבחינת הנגישות הפיזית
                    וההנדסית של המבנה וסביבתו. הבדיקה מתייחסת
                    לדרכי הגישה, לכניסה למקום ולשטחים השונים
                    שבהם ניתן שירות לציבור.
                  </p>

                  <h3 className="accessibility-sub-title">
                    אילו התאמות נגישות נבדקות?
                  </h3>

                  <ul className="accessibility-list">
                    <li>חניות נכים ודרכי גישה נגישות</li>
                    <li>שיפועים ורמפות בכניסה לעסק</li>
                    <li>רוחב פתחים ודלתות</li>
                    <li>מעברים פנימיים ונגישות בין אזורי העסק</li>
                    <li>התאמות נגישות בתאי השירותים, ככל שנדרש</li>
                  </ul>

                </div>
              </div>

              <div className="accessibility-expert-box">

                <div className="accessibility-expert-number">
                  2
                </div>

                <div className="accessibility-expert-content">

                  <h2 className="accessibility-expert-title">
                    מורשה נגישות השירות לעסקים
                  </h2>

                  <p className="accessibility-info-text">
                    מורשה נגישות השירות מתמקד בנגישות השירות
                    הניתן ללקוחות העסק, באופן קבלת השירות
                    ובהתאמת סביבת השירות לאנשים עם מוגבלויות.
                  </p>

                  <h3 className="accessibility-sub-title">
                    אילו נושאים נבדקים בתחום נגישות השירות?
                  </h3>

                  <ul className="accessibility-list">
                    <li>גובה דלפקי שירות וקופות, בהתאם לדרישות</li>
                    <li>התאמת ריהוט ועמדות שירות</li>
                    <li>מערכות עזר לשמיעה, כגון לולאות השראה</li>
                    <li>שילוט ברור ומכוון</li>
                    <li>התאמות נגישות באמצעי השירות הדיגיטליים, ככל שנדרש</li>
                    <li>הדרכת עובדים למתן שירות נגיש</li>
                  </ul>

                </div>
              </div>

              <div className="accessibility-process-section">

                <h2 className="accessibility-section-title">
                  תהליך בדיקת נגישות לעסק מול הרשות המקומית
                </h2>

                <div className="accessibility-process-item">

                  <span className="accessibility-process-number">
                    01
                  </span>

                  <div>

                    <h3 className="accessibility-process-title">
                      בדיקת מסמכים וחוות דעת
                    </h3>

                    <p className="accessibility-small-info-text">
                      המסמכים וחוות הדעת המקצועיות נבחנים בהתאם
                      לדרישות הדין ולדרישות הרשות המוסמכת.
                    </p>

                  </div>
                </div>

                <div className="accessibility-process-item">

                  <span className="accessibility-process-number">
                    02
                  </span>

                  <div>

                    <h3 className="accessibility-process-title">
                      בדיקת התאמות הנגישות בעסק
                    </h3>

                    <p className="accessibility-small-info-text">
                      בהתאם לסוג העסק ולנסיבות, עשויה להתבצע
                      בדיקה במקום כדי לוודא שההתאמות הנדרשות
                      בוצעו בהתאם לתכנון ולדרישות הרלוונטיות.
                    </p>

                  </div>
                </div>

                <div className="accessibility-process-item">

                  <span className="accessibility-process-number">
                    03
                  </span>

                  <div>

                    <h3 className="accessibility-process-title">
                      השלמת דרישות הנגישות
                    </h3>

                    <p className="accessibility-small-info-text">
                      לאחר השלמת הבדיקות והמסמכים הנדרשים,
                      ניתן להתקדם בתהליך הרישוי בהתאם
                      להחלטות הרשות ולהוראות הדין.
                    </p>

                  </div>
                </div>

              </div>

              <div className="accessibility-golden-tip">

                <div className="accessibility-tip-icon">
                  ★
                </div>

                <div>

                  <h3 className="accessibility-tip-title">
                    טיפ חשוב לבעלי עסקים
                  </h3>

                  <p className="accessibility-tip-text">
                    מומלץ לבדוק את דרישות הנגישות כבר בשלב
                    התכנון הראשוני של העסק, ולפני ביצוע שיפוץ.
                    תכנון מוקדם עשוי למנוע טעויות, הוצאות
                    מיותרות ועיכובים בהמשך תהליך הרישוי.
                  </p>

                </div>
              </div>

            </div>

            <div className="accessibility-requirements-section">

              <h2 className="accessibility-section-title">
                מה נדרש מבעל העסק לצורך התאמות נגישות?
              </h2>

              <div className="accessibility-items">

                <div className="accessibility-item">

                  <div className="accessibility-icon">
                    ✓
                  </div>

                  <div>

                    <h3 className="accessibility-item-title">
                      בדיקת נגישות לעסק
                    </h3>

                    <p className="accessibility-item-text">
                      בחינת מאפייני העסק והתאמתו לדרישות
                      הנגישות הקבועות בחוק ובתקנות הרלוונטיות.
                    </p>

                  </div>
                </div>

                <div className="accessibility-item">

                  <div className="accessibility-icon">
                    ✓
                  </div>

                  <div>

                    <h3 className="accessibility-item-title">
                      חוות דעת מקצועית
                    </h3>

                    <p className="accessibility-item-text">
                      קבלת חוות דעת ממורשי נגישות מוסמכים,
                      בהתאם לסוג העסק ולדרישות החלות עליו.
                    </p>

                  </div>
                </div>

                <div className="accessibility-item">

                  <div className="accessibility-icon">
                    ✓
                  </div>

                  <div>

                    <h3 className="accessibility-item-title">
                      ביצוע התאמות נגישות
                    </h3>

                    <p className="accessibility-item-text">
                      ביצוע ההתאמות הנדרשות לצורך עמידה
                      בהוראות הדין ובהליכי רישוי העסק.
                    </p>

                  </div>
                </div>

              </div>
            </div>

            <div className="accessibility-bottom-note">

              <span className="accessibility-note-icon">
                💡
              </span>

              <span>
                בדיקה מוקדמת של דרישות הנגישות יכולה לסייע
                לבעלי עסקים להיערך נכון ולצמצם עיכובים בתהליך הרישוי.
              </span>

            </div>

          </div>
        </div>
      </div>
    </Container>
  );
};
